import { format, PAYMENTS_PER_YEAR, pmt, RepaymentFrequency } from '@/lib/calculators/pmtUtils';

// ─── Finance Lease ────────────────────────────────────────────────────────────

export type FinanceLeaseInput = {
  assetPrice: number;
  residualValue: number;
  annualRate: number; // %
  loanTermYears: number;
  repaymentFrequency: RepaymentFrequency;
  businessUsePercent: number; // 0–100
};

export type FinanceLeaseResult = {
  leasePayment: string;
  paymentLabel: string;
  totalPayments: string;
  financeCost: string;
  financeCostRaw: number;
  taxDeductPerYear: string;
  residualValue: string;
};

const PAYMENT_LABELS: Record<RepaymentFrequency, string> = {
  monthly: 'Monthly',
  fortnightly: 'Fortnightly',
  weekly: 'Weekly',
};

function calculateFinanceLease({
  assetPrice,
  residualValue,
  annualRate,
  loanTermYears,
  repaymentFrequency,
  businessUsePercent,
}: FinanceLeaseInput): FinanceLeaseResult {
  const paymentsPerYear = PAYMENTS_PER_YEAR[repaymentFrequency];
  const periodicRate = annualRate / 100 / paymentsPerYear;
  const nPeriods = loanTermYears * paymentsPerYear;

  if (assetPrice <= 0 || loanTermYears <= 0) {
    return {
      leasePayment: '0.00',
      paymentLabel: PAYMENT_LABELS[repaymentFrequency],
      totalPayments: '0.00',
      financeCost: '0.00',
      financeCostRaw: 0,
      taxDeductPerYear: '0.00',
      residualValue: format(residualValue),
    };
  }

  const leasePayment = pmt(periodicRate, nPeriods, -assetPrice, residualValue);
  const totalPayments = leasePayment * nPeriods;
  const financeCostRaw = totalPayments + residualValue - assetPrice;
  const taxDeductPerYear = leasePayment * paymentsPerYear * (businessUsePercent / 100);

  return {
    leasePayment: format(leasePayment),
    paymentLabel: PAYMENT_LABELS[repaymentFrequency],
    totalPayments: format(totalPayments),
    financeCost: format(Math.max(financeCostRaw, 0)),
    financeCostRaw: Math.max(financeCostRaw, 0),
    taxDeductPerYear: format(Math.max(taxDeductPerYear, 0)),
    residualValue: format(residualValue),
  };
}

// ─── Guarantor Loan ───────────────────────────────────────────────────────────

export type GuarantorLoanInput = {
  loanAmount: number;
  loanTermYears: number;
  securedRate: number; // % — with-guarantor rate
  unsecuredRate: number; // % — without-guarantor rate
  repaymentFrequency: RepaymentFrequency;
  guarantorPropertyValue: number;
  existingMortgage: number;
};

export type GuarantorLoanResult = {
  securedPayment: string;
  unsecuredPayment: string;
  paymentLabel: string;
  repaymentSaving: string;
  securedTotalInterest: string;
  unsecuredTotalInterest: string;
  securedTotalInterestRaw: number;
  unsecuredTotalInterestRaw: number;
  totalInterestSaving: string;
  usableEquity: string;
};

function calculateGuarantorLoan({
  loanAmount,
  loanTermYears,
  securedRate,
  unsecuredRate,
  repaymentFrequency,
  guarantorPropertyValue,
  existingMortgage,
}: GuarantorLoanInput): GuarantorLoanResult {
  const freq = PAYMENTS_PER_YEAR[repaymentFrequency];
  const nPeriods = loanTermYears * freq;
  const usableEquity = Math.max(guarantorPropertyValue * 0.8 - existingMortgage, 0);

  if (loanAmount <= 0 || loanTermYears <= 0) {
    return {
      securedPayment: '0.00',
      unsecuredPayment: '0.00',
      paymentLabel: PAYMENT_LABELS[repaymentFrequency],
      repaymentSaving: '0.00',
      securedTotalInterest: '0.00',
      unsecuredTotalInterest: '0.00',
      securedTotalInterestRaw: 0,
      unsecuredTotalInterestRaw: 0,
      totalInterestSaving: '0.00',
      usableEquity: format(usableEquity),
    };
  }

  const securedRepayment = pmt(securedRate / 100 / freq, nPeriods, -loanAmount);
  const unsecuredRepayment = pmt(unsecuredRate / 100 / freq, nPeriods, -loanAmount);
  const securedTotalInt = Math.max(securedRepayment * nPeriods - loanAmount, 0);
  const unsecuredTotalInt = Math.max(unsecuredRepayment * nPeriods - loanAmount, 0);
  const repaymentSaving = Math.max(unsecuredRepayment - securedRepayment, 0);
  const totalInterestSaving = Math.max(unsecuredTotalInt - securedTotalInt, 0);

  return {
    securedPayment: format(securedRepayment),
    unsecuredPayment: format(unsecuredRepayment),
    paymentLabel: PAYMENT_LABELS[repaymentFrequency],
    repaymentSaving: format(repaymentSaving),
    securedTotalInterest: format(securedTotalInt),
    unsecuredTotalInterest: format(unsecuredTotalInt),
    securedTotalInterestRaw: securedTotalInt,
    unsecuredTotalInterestRaw: unsecuredTotalInt,
    totalInterestSaving: format(totalInterestSaving),
    usableEquity: format(usableEquity),
  };
}

// ─── Lease vs Loan ────────────────────────────────────────────────────────────

export type LeaseVsLoanInput = {
  financedAmount: number;
  loanTermYears: number;
  leaseRate: number; // % annual
  loanRate: number; // % annual
  residualValue: number; // for lease FV
  balloonPaymentPercent: number; // for loan
  repaymentFrequency: RepaymentFrequency;
};

export type LeaseVsLoanResult = {
  leasePayment: string;
  loanPayment: string;
  paymentLabel: string;
  leaseFinanceCost: string;
  loanFinanceCost: string;
  cheaperOption: 'lease' | 'loan' | 'equal';
  cheaperByAmount: string;
};

function calculateLeaseVsLoan({
  financedAmount,
  loanTermYears,
  leaseRate,
  loanRate,
  residualValue,
  balloonPaymentPercent,
  repaymentFrequency,
}: LeaseVsLoanInput): LeaseVsLoanResult {
  const freq = PAYMENTS_PER_YEAR[repaymentFrequency];
  const nPeriods = loanTermYears * freq;
  const balloonAmount = (balloonPaymentPercent / 100) * financedAmount;

  if (financedAmount <= 0 || loanTermYears <= 0) {
    return {
      leasePayment: '0.00',
      loanPayment: '0.00',
      paymentLabel: PAYMENT_LABELS[repaymentFrequency],
      leaseFinanceCost: '0.00',
      loanFinanceCost: '0.00',
      cheaperOption: 'equal',
      cheaperByAmount: '0.00',
    };
  }

  const leasePMT = pmt(leaseRate / 100 / freq, nPeriods, -financedAmount, residualValue);
  const loanPMT = pmt(loanRate / 100 / freq, nPeriods, -financedAmount, balloonAmount);
  const leaseFinCostRaw = Math.max(leasePMT * nPeriods - financedAmount, 0);
  const loanFinCostRaw = Math.max(loanPMT * nPeriods + balloonAmount - financedAmount, 0);

  const leaseRounded = Math.round(leaseFinCostRaw * 100) / 100;
  const loanRounded = Math.round(loanFinCostRaw * 100) / 100;
  const cheaperOption: 'lease' | 'loan' | 'equal' =
    leaseRounded < loanRounded ? 'lease' : loanRounded < leaseRounded ? 'loan' : 'equal';
  const cheaperByAmount = Math.abs(leaseFinCostRaw - loanFinCostRaw);

  return {
    leasePayment: format(leasePMT),
    loanPayment: format(loanPMT),
    paymentLabel: PAYMENT_LABELS[repaymentFrequency],
    leaseFinanceCost: format(leaseFinCostRaw),
    loanFinanceCost: format(loanFinCostRaw),
    cheaperOption,
    cheaperByAmount: format(cheaperByAmount),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAssetFinanceCalculators() {
  return {
    calculateFinanceLease,
    calculateGuarantorLoan,
    calculateLeaseVsLoan,
  };
}
